/**
 * ============================================================================
 * PERFORMANCE UTILITIES
 * ============================================================================
 *
 * Modern performance optimization utilities.
 *
 * ============================================================================
 */

/**
 * Intersection Observer for lazy loading
 * @param {Element} element - Element to observe
 * @param {Function} callback - Callback when element is visible
 * @param {Object} options - Observer options
 */
function observeIntersection(element, callback, options = {}) {
  const defaultOptions = {
    root: null,
    rootMargin: '50px',
    threshold: 0.1
  };

  const observer = new IntersectionObserver((entries) => {
    entries.forEach(entry => {
      if (entry.isIntersecting) {
        callback(entry.target);
        observer.unobserve(entry.target);
      }
    });
  }, { ...defaultOptions, ...options });

  observer.observe(element);
  return observer;
}

/**
 * Preload critical resources
 * @param {string} href - Resource URL
 * @param {string} as - Resource type
 * @param {string} type - MIME type
 */
function preloadResource(href, as, type = null) {
  const link = document.createElement('link');
  link.rel = 'preload';
  link.href = href;
  link.as = as;
  if (type) link.type = type;

  document.head.appendChild(link);
}

/**
 * Prefetch resources for next navigation
 * @param {string} href - Resource URL
 */
function prefetchResource(href) {
  const link = document.createElement('link');
  link.rel = 'prefetch';
  link.href = href;

  document.head.appendChild(link);
}

/**
 * Web Vitals measurement
 */
class WebVitals {
  constructor() {
    this.metrics = new Map();
    this.init();
  }

  init() {
    // Largest Contentful Paint
    this.observeLCP();

    // First Input Delay
    this.observeFID();

    // Cumulative Layout Shift
    this.observeCLS();
  }

  observeLCP() {
    if ('PerformanceObserver' in window) {
      const observer = new PerformanceObserver((list) => {
        const entries = list.getEntries();
        const lastEntry = entries[entries.length - 1];
        this.metrics.set('LCP', lastEntry.startTime);
      });

      observer.observe({ entryTypes: ['largest-contentful-paint'] });
    }
  }

  observeFID() {
    if ('PerformanceObserver' in window) {
      const observer = new PerformanceObserver((list) => {
        const entries = list.getEntries();
        entries.forEach(entry => {
          this.metrics.set('FID', entry.processingStart - entry.startTime);
        });
      });

      observer.observe({ entryTypes: ['first-input'] });
    }
  }

  observeCLS() {
    if ('PerformanceObserver' in window) {
      let clsValue = 0;

      const observer = new PerformanceObserver((list) => {
        const entries = list.getEntries();
        entries.forEach(entry => {
          if (!entry.hadRecentInput) {
            clsValue += entry.value;
            this.metrics.set('CLS', clsValue);
          }
        });
      });

      observer.observe({ entryTypes: ['layout-shift'] });
    }
  }

  getMetrics() {
    return Object.fromEntries(this.metrics);
  }
}

/**
 * Resource hints for better performance
 */
class ResourceHints {
  static preconnect(origin) {
    const link = document.createElement('link');
    link.rel = 'preconnect';
    link.href = origin;
    link.crossOrigin = 'anonymous';
    document.head.appendChild(link);
  }

  static dnsPrefetch(origin) {
    const link = document.createElement('link');
    link.rel = 'dns-prefetch';
    link.href = origin;
    document.head.appendChild(link);
  }

  static modulePreload(href) {
    const link = document.createElement('link');
    link.rel = 'modulepreload';
    link.href = href;
    document.head.appendChild(link);
  }
}

/**
 * Critical resource loading
 */
function loadCriticalCSS(href) {
  const link = document.createElement('link');
  link.rel = 'stylesheet';
  link.href = href;
  link.media = 'print';
  link.onload = function() {
    this.media = 'all';
  };

  document.head.appendChild(link);
}

/**
 * Lazy load images with modern techniques
 * @param {string} selector - Image selector
 */
function lazyLoadImages(selector = 'img[data-src]') {
  const images = document.querySelectorAll(selector);

  if ('loading' in HTMLImageElement.prototype) {
    // Native lazy loading support
    images.forEach(img => {
      img.src = img.dataset.src;
      img.removeAttribute('data-src');
    });
  } else {
    // Fallback to Intersection Observer
    images.forEach(img => {
      observeIntersection(img, (target) => {
        target.src = target.dataset.src;
        target.removeAttribute('data-src');
        target.classList.add('loaded');
      });
    });
  }
}

/**
 * Performance budget monitoring
 */
class PerformanceBudget {
  constructor(budgets = {}) {
    this.budgets = {
      lcp: 2500,
      fid: 100,
      cls: 0.1,
      ...budgets
    };
  }

  check(metrics) {
    const violations = [];

    Object.entries(this.budgets).forEach(([metric, budget]) => {
      if (metrics[metric] && metrics[metric] > budget) {
        violations.push({
          metric,
          value: metrics[metric],
          budget,
          exceeded: metrics[metric] - budget
        });
      }
    });

    return violations;
  }
}

/**
 * Initialize performance monitoring
 */
function initPerformanceMonitoring() {
  const webVitals = new WebVitals();
  const budget = new PerformanceBudget();

  // Setup resource hints for common origins
  ResourceHints.preconnect('https://cdn.shopify.com');
  ResourceHints.dnsPrefetch('https://fonts.googleapis.com');

  // Monitor performance after page load
  window.addEventListener('load', () => {
    setTimeout(() => {
      const metrics = webVitals.getMetrics();
      const violations = budget.check(metrics);



      // Send metrics to analytics (if needed)
      if (window.gtag) {
        Object.entries(metrics).forEach(([metric, value]) => {
          window.gtag('event', 'web_vitals', {
            metric_name: metric,
            metric_value: Math.round(value),
            custom_parameter: 'custom_value'
          });
        });
      }
    }, 1000);
  });
}
